import { fetchCurrentUser } from '../../ducks/user.duck';
import { uploadListingImage } from '../../util/listingImageUpload';
import { convertToJpeg } from '../../util/imageWatermark';
import { MAX_PHOTOS, newPhotoId, validatePhoto, processListingPhoto } from './listingPhotos';

const UPDATE = 'app/NewListingPage/UPDATE';
const RESET = 'app/NewListingPage/RESET';
const initialState = {
  photos: [],
  sessionId: null,
  ownerId: null,
  batch: false,
  uploading: false,
  retryAt: 0,
  error: null,
  handoff: null,
  listingId: null,
};
export const updatePhotos = payload => ({ type: UPDATE, payload });
export const resetPhotos = () => ({ type: RESET });

export default function reducer(state = initialState, action = {}) {
  if (action.type === RESET) return initialState;
  if (action.type === UPDATE) return { ...state, ...action.payload };
  return state;
}

const updatePhoto = (id, patch) => (dispatch, getState) => {
  const state = getState().NewListingPage;
  dispatch(updatePhotos({ photos: state.photos.map(p => (p.id === id ? { ...p, ...patch } : p)) }));
};

export const addPhotos = files => (dispatch, getState) => {
  const state = getState().NewListingPage;
  if (state.batch || state.uploading || state.listingId) return;
  const photos = [...state.photos];
  let error = null;
  Array.from(files).forEach(file => {
    const invalid = validatePhoto(file);
    if (invalid) {
      error = invalid;
      return;
    }
    if (
      photos.some(
        p =>
          p.original.name === file.name &&
          p.original.size === file.size &&
          p.original.lastModified === file.lastModified
      )
    )
      return;
    if (photos.length >= MAX_PHOTOS) {
      error = 'too_many_images';
      return;
    }
    photos.push({
      id: newPhotoId(),
      original: file,
      generated: null,
      selected: 'original',
      status: 'waiting',
      error: null,
    });
  });
  dispatch(
    updatePhotos({
      photos,
      error,
      sessionId: state.sessionId || newPhotoId(),
      ownerId: getState().user.currentUser.id.uuid,
      handoff: null,
    })
  );
};

export const editPhoto = (id, operation) => (dispatch, getState) => {
  const state = getState().NewListingPage;
  const index = state.photos.findIndex(p => p.id === id);
  const photo = state.photos[index];
  if (
    !photo ||
    state.uploading ||
    state.listingId ||
    (photo.status === 'processing' && operation === 'remove')
  )
    return;
  let photos = [...state.photos];
  if (operation === 'remove') photos.splice(index, 1);
  else if (operation === 'original' || (operation === 'generated' && photo.generated))
    photos[index] = { ...photo, selected: operation };
  else if (operation === 'earlier' || operation === 'later') {
    const to = index + (operation === 'earlier' ? -1 : 1);
    if (to >= 0 && to < photos.length) [photos[index], photos[to]] = [photos[to], photos[index]];
  }
  dispatch(updatePhotos({ photos, handoff: null, error: null }));
};

export const preparePhoto = (id, regenerate = false) => async (dispatch, getState) => {
  const state = getState().NewListingPage;
  const photo = state.photos.find(p => p.id === id);
  if (
    !photo ||
    state.uploading ||
    state.listingId ||
    state.retryAt > Date.now() ||
    state.photos.some(p => p.status === 'processing') ||
    (photo.generated && !regenerate)
  )
    return;
  const sessionId = state.sessionId;
  dispatch(updatePhoto(id, { status: 'processing', error: null }));
  try {
    const generated = await processListingPhoto(photo.original);
    if (getState().NewListingPage.sessionId === sessionId) {
      // Review starts on the original; opting into the generated version is explicit.
      dispatch(updatePhoto(id, { generated, selected: 'original', status: 'ready', error: null }));
    }
  } catch (e) {
    if (getState().NewListingPage.sessionId === sessionId) {
      dispatch(updatePhoto(id, { status: 'error', error: e.message }));
      if (e.retryAfter) dispatch(updatePhotos({ retryAt: Date.now() + e.retryAfter * 1000 }));
    }
    return e;
  }
};

export const preparePhotos = () => async (dispatch, getState) => {
  const state = getState().NewListingPage;
  if (
    state.batch ||
    state.uploading ||
    state.retryAt > Date.now() ||
    state.listingId ||
    state.photos.some(p => p.status === 'processing')
  )
    return;
  dispatch(updatePhotos({ batch: true, error: null }));
  for (const photo of state.photos.filter(p => !p.generated)) {
    if (getState().NewListingPage.sessionId !== state.sessionId) return;
    const error = await dispatch(preparePhoto(photo.id));
    if (
      error &&
      (error.status === 429 ||
        [
          'provider_quota_exceeded',
          'unavailable',
          'authentication_required',
          'permission_required',
        ].includes(error.message))
    )
      break;
  }
  if (getState().NewListingPage.sessionId === state.sessionId)
    dispatch(updatePhotos({ batch: false }));
};

export const acceptPhotos = (config, originalsOnly = false) => async (dispatch, getState, sdk) => {
  const state = getState().NewListingPage;
  if (
    state.uploading ||
    state.batch ||
    state.photos.some(p => p.status === 'processing') ||
    !state.photos.length
  )
    return null;
  if (state.listingId) return state;
  dispatch(updatePhotos({ uploading: true, error: null }));
  const images = [];
  try {
    for (const photo of state.photos) {
      const selected = originalsOnly ? 'original' : photo.selected;
      const file = photo[selected];
      // Reuse successful uploads after a partial failure or browser Back, per version.
      const cached = photo.uploads?.[selected];
      let entity = cached?.source === file ? cached.entity : null;
      if (!entity) {
        const uploadFile = await convertToJpeg(file);
        const response = await uploadListingImage(sdk, uploadFile, config.layout.listingImage);
        entity = {
          ...response.data.data,
          id: photo.id,
          imageId: response.data.data.id,
          file: uploadFile,
        };
        if (getState().NewListingPage.sessionId !== state.sessionId) return null;
        dispatch(
          updatePhoto(photo.id, {
            uploads: { ...photo.uploads, [selected]: { source: file, entity } },
          })
        );
      }
      images.push(entity);
    }
    if (getState().NewListingPage.sessionId !== state.sessionId) return null;
    const handoff = { sessionId: state.sessionId, ownerId: state.ownerId, images };
    dispatch(updatePhotos({ handoff, uploading: false }));
    return handoff;
  } catch (e) {
    if (getState().NewListingPage.sessionId === state.sessionId)
      dispatch(updatePhotos({ uploading: false, error: 'upload_failed' }));
    return null;
  }
};

export const loadData = () => dispatch =>
  dispatch(fetchCurrentUser({ updateNotifications: false }));
